import { newRequestKey } from "../../../messaging/requestKey";
import { apiPost } from "./apiClient";
import { businessSendOutcome } from '../../../../features/switchover/models/businessSendOutcome';
import { validResolution } from '../../../../features/switchover/models/businessResolutionApi';
import { assertSessionCurrent, sessionTicket } from '../../../../utils/sessionLifecycle';

const flights = new Map();
const scope = (actor, query, action) => `uzdpart360:business-send:${actor}:${query}:${action}`;
const copy = value => JSON.parse(JSON.stringify(value));
// Requests saved before the server stopped reading body userId still carry it.
const withoutActor = body => { const { userId, ...rest } = body || {}; return rest; };
const stable = value => JSON.stringify(value, (_, item) => item && typeof item === "object" && !Array.isArray(item)
  ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
const blocked = error => ({ state: "blocked", error, storageBlocked: true });
const announce = () => window.dispatchEvent(new Event("business-send-updated"));
// The server answered with a definite refusal (validation, permission, missing
// resource): nothing was sent, so the attempt must not fence later sends.
// Messaging/reconciliation/uncertain codes and 409s stay fenced.
const DEFINITE_REFUSAL_STATUSES = new Set([400, 403, 404, 422]);
export function isDefiniteRefusal(error) {
  const status = Number(error?.response?.status);
  const code = String(error?.response?.data?.data?.code || error?.response?.data?.code || error?.response?.data?.error?.code || "");
  return DEFINITE_REFUSAL_STATUSES.has(status) && !code.startsWith("MESSAGING_") && !/RECONCILIATION|UNCERTAIN/.test(code);
}

export function loadBusinessSend(actor, query, action) {
  try {
    const raw = localStorage.getItem(scope(actor, query, action));
    if (raw === null) return null;
    const saved = JSON.parse(raw);
    if (saved?.schema !== 1 || String(saved.actor) !== String(actor) || String(saved.query) !== String(query)
      || saved.action !== action || saved.path !== `/queries/${query}/${action}`
      || !/^[a-zA-Z0-9._:-]{1,128}$/.test(saved.key || "") || !saved.body || typeof saved.body !== "object" || Array.isArray(saved.body)
      || (saved.body.userId != null && String(saved.body.userId) !== String(actor)) || !["pending", "uncertain", "blocked", "completed", "verified_resolved"].includes(saved.state)
      || (saved.state === 'verified_resolved' && (!validResolution(saved.resolution) || saved.resolution.operation_id !== saved.operation_id || String(saved.resolution.actor_user_id) !== String(actor) || saved.resolution.operation_type !== operationTypes[action]))
      || typeof saved.durable !== "boolean"
      || typeof saved.body.message_text !== "string" || !saved.body.message_text.trim()
      || (saved.state === "completed" && (saved.result?.success !== true || (saved.durable && !validOperation(saved.result.messaging_operation, action))
        || (!saved.durable && saved.result.messaging_operation)))) return blocked("Saved send state is invalid. Verify the existing outcome before sending again.");
    return saved;
  } catch { return blocked("Saved send state cannot be read. Sending is blocked until the existing outcome is verified."); }
}

function persist(attempt) {
  const key = scope(attempt.actor, attempt.query, attempt.action);
  localStorage.setItem(key, JSON.stringify(attempt));
  if (localStorage.getItem(key) !== JSON.stringify(attempt)) throw new Error("The frozen send request could not be saved. Nothing was sent.");
  announce();
}

function assertActor(actor) {
  const auth = JSON.parse(localStorage.getItem("auth") || "null");
  if (!auth?.isAuthenticated || String(auth.id) !== String(actor)) throw new Error("The saved send belongs to a different authenticated user. Sign in again before sending.");
}

export function startNewBusinessSend(actor, query, action) {
  assertActor(actor);
  const saved = loadBusinessSend(actor, query, action);
  if (!["completed", 'verified_resolved'].includes(saved?.state) || saved.storageBlocked) throw new Error("An unresolved send cannot be replaced.");
  localStorage.removeItem(scope(actor, query, action));
  announce();
}

const operationTypes = { "share-quote": "query_direct_quote", "notify-unavailable-parts": "query_unavailable_parts", "request-billing-details": "query_billing_details" };
function validOperation(op, action) {
  return /^op_[a-f0-9]{32}$/.test(op?.id || "") && op.resource_type === "messaging_operation"
    && op.operation_type === operationTypes[action] && /^con_[a-f0-9]{32}$/.test(op.conversation_id || "")
    && /^msg_[a-f0-9]{32}$/.test(op.message_id || "") && typeof op.client_message_id === "string"
    && op.status === "submitted" && Number.isFinite(Date.parse(op.accepted_at));
}

// Keep the key/body for reconciliation, but do not replay unresolved sends through
// gate-selected endpoints: rollback can route even a durable key to legacy sending.
export function runBusinessSend(actor, query, action, body) {
  const ticket = sessionTicket();
  const slot = scope(actor, query, action);
  let frozenBody;
  try { frozenBody = copy(body); } catch { return Promise.reject(new Error("The send request could not be frozen.")); }
  try { assertActor(actor); } catch (error) { return Promise.reject(error); }
  if (flights.has(slot)) {
    const current = flights.get(slot);
    return stable(current.body) === stable(frozenBody) ? current.promise : Promise.reject(new Error("A different frozen request is already pending."));
  }
  const execute = async () => {
    assertSessionCurrent(ticket);
    assertActor(actor);
    if (!operationTypes[action] || !/^[1-9]\d*$/.test(String(query)) ) throw new Error("Invalid business-send identity.");
    let attempt = loadBusinessSend(actor, query, action);
    if (attempt?.storageBlocked) throw new Error(attempt.error);
    if (attempt) {
      if (stable(withoutActor(attempt.body)) !== stable(withoutActor(frozenBody))) throw new Error("A frozen request already exists. Recover that exact request; do not change its text or commercial details.");
      if (attempt.state === "completed") return copy(attempt.result);
      throw new Error("A send is unresolved. Its original request is retained; retry is blocked until safe recovery is available.");
    } else {
      attempt = { schema: 1, actor: String(actor), query: String(query), action, path: `/queries/${query}/${action}`,
        body: frozenBody, key: newRequestKey(), state: "pending", durable: false, createdAt: new Date().toISOString() };
    }
    attempt = { ...attempt, state: "pending", error: null };
    persist(attempt); // must succeed BEFORE dispatch
    try {
      const response = await apiPost(attempt.path, copy(attempt.body), { headers: { "Idempotency-Key": attempt.key } });
      const result = response?.data;
      const hasOperation = Object.prototype.hasOwnProperty.call(result || {}, "messaging_operation");
      if (result?.success !== true || (attempt.durable && !hasOperation) || (hasOperation && !validOperation(result.messaging_operation, action))) {
        const error = new Error(result?.message || "Malformed send response; verify the existing outcome.");
        error.response = { ...response, data: result };
        throw error;
      }
      attempt = { ...attempt, state: "completed", durable: hasOperation || attempt.durable, result: copy(result), error: null };
      persist(attempt);
      return copy(result);
    } catch (error) {
      if (isDefiniteRefusal(error)) {
        try { localStorage.removeItem(scope(actor, query, action)); } finally { announce(); }
        throw new Error(error?.response?.data?.message || error.message || "The server refused this send.");
      }
      const code = error?.response?.data?.data?.code || error?.response?.data?.error?.code;
      const outcome = businessSendOutcome(error);
      attempt = { ...attempt, state: "uncertain", durable: attempt.durable || Boolean(code?.startsWith("MESSAGING_")),
        operation_id: outcome?.operation_id || attempt.operation_id || null,
        error: `${outcome?.message || error?.response?.data?.message || error.message || "Send outcome unknown"} — original request retained. Provider acceptance is not delivery. Do not start another send until reconciled.` };
      try { persist(attempt); } catch { /* the pre-dispatch pending record remains a fail-closed fence */ }
      throw Object.assign(new Error(attempt.error), { code, delivery: outcome?.delivery, operation_id: attempt.operation_id });
    }
  };
  // Cross-tab serialization where Web Locks is available; server idempotency
  // remains the final guard. No automatic retries or expiry-based key rotation.
  const flight = navigator.locks?.request ? navigator.locks.request(slot, execute) : execute();
  flights.set(slot, { promise: flight, body: frozenBody });
  flight.finally(() => { if (flights.get(slot)?.promise === flight) { flights.delete(slot); announce(); } }).catch(() => {});
  return flight;
}

// True while this tab is still waiting for the send's response. The saved
// "pending" fence is normal then; only a send left pending without a live
// request needs the reconciliation warning.
export function businessSendInFlight(actor, query, action) {
  return flights.has(scope(actor, query, action));
}

export function replayBusinessSend(actor, query, action) {
  const attempt = loadBusinessSend(actor, query, action);
  if (!attempt?.body || attempt.storageBlocked) return Promise.reject(new Error(attempt?.error || "No saved request."));
  return runBusinessSend(actor, query, action, attempt.body);
}
